import React, { useState, useEffect, useRef } from 'react';
import { X, Terminal, Trash2, Download, Copy, Play } from 'lucide-react';
import './ConsoleOverlay.css';

const ConsoleOverlay = ({ onClose }) => {
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState('connecting');
  const scrollRef = useRef(null);
  const eventSourceRef = useRef(null);

  useEffect(() => {
    // 1. Charger les logs récents
    fetch('/api/logs/recent')
      .then(res => res.json())
      .then(data => setLogs(data || []))
      .catch(err => console.error("Logs error", err));

    // 2. Ouvrir le flux SSE
    const es = new EventSource('/api/logs/stream');
    eventSourceRef.current = es;

    es.onopen = () => setStatus('connected');
    
    es.onmessage = (event) => {
      setLogs(prev => [...prev, event.data].slice(-200)); // Garder max 200 lignes
    };

    es.onerror = () => {
      setStatus('error');
      // Tentative de reconnexion auto gérée par le navigateur
    };

    return () => {
      if (eventSourceRef.current) eventSourceRef.current.close();
    };
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs]);

  const clearLogs = () => setLogs([]);

  const copyToClipboard = () => {
    const text = logs.join('\n');
    navigator.clipboard.writeText(text);
  };

  const downloadLogs = () => {
    const text = logs.join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `instasto_logs_${new Date().toISOString().slice(0,19)}.log`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="console-overlay fade-in">
      <div className="console-window glass-effect scale-in">
        <header className="console-header">
          <div className="console-title">
            <Terminal size={18} className="terminal-icon" />
            <span className="title-text">CONSOLE SERVEUR VPS</span>
            <div className={`status-dot ${status}`}></div>
            <span className="status-text">{status.toUpperCase()}</span>
          </div>
          
          <div className="console-actions">
            <button className="icon-btn" onClick={copyToClipboard} title="Copier">
              <Copy size={16} />
            </button>
            <button className="icon-btn" onClick={downloadLogs} title="Télécharger">
              <Download size={16} />
            </button>
            <button className="icon-btn" onClick={clearLogs} title="Effacer">
              <Trash2 size={16} />
            </button>
            <div className="divider"></div>
            <button className="console-close-btn" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </header>

        <div className="console-body" ref={scrollRef}>
          {logs.length === 0 ? (
            <div className="console-empty">
              <Play size={32} opacity={0.2} />
              <p>Attente des nouveaux logs...</p>
            </div>
          ) : (
            <div className="logs-container">
              {logs.map((log, i) => {
                // Coloration basique
                let typeClass = "";
                if (log.toLowerCase().includes("error") || log.toLowerCase().includes("failed") || log.includes("❌")) typeClass = "log-error";
                else if (log.toLowerCase().includes("warning") || log.includes("⚠️")) typeClass = "log-warning";
                else if (log.includes("✅") || log.includes("SUCCESS")) typeClass = "log-success";
                else if (log.includes("DEBUG") || log.includes("📂")) typeClass = "log-debug";

                return (
                  <div key={i} className={`log-line ${typeClass}`}>
                    <span className="line-content">{log}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <footer className="console-footer">
          <div className="footer-info">
            <span>MEM: 200L max (server.log)</span>
            <span>Uptime: {Math.floor(performance.now() / 60000)}m</span>
          </div>
          <div className="footer-command">
            <span className="prompt">$</span>
            <span className="cursor-block"></span>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ConsoleOverlay;
